/**
 * POS Appearance — Sprint 7 (Premium UX Sprint)
 *
 * Consolidated appearance section that governs the full POS terminal
 * theme: colour, typography, shape and elevation. Sits inside
 * `/pos/settings` under the standard admin shell.
 *
 * Design goals:
 *   • The preview uses the EXACT SAME resolver (`resolvePosTheme`) and
 *     CSS variable map as the live terminal, mounted on a scoped root.
 *     Preview and terminal cannot disagree.
 *   • Every configurable slot has an auto text-on-fill readout so
 *     admins can see the light-fill/dark-text rule apply live.
 *   • Operational status colours (success/warning/destructive, offline,
 *     KOT unsent) are NOT exposed — they stay system-controlled.
 *   • "View on Terminal" saves the theme and opens the outlet's POS in
 *     a new tab so the funder sees the actual terminal, not a mockup.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Palette, RotateCcw, Save, ExternalLink, Type, Square, Layers } from 'lucide-react';
import {
  resolvePosTheme,
  applyPosThemeVars,
  ensurePosFontLoaded,
  bestTextOn,
  contrastRatio,
} from '../pos/posTheme';

const COLOUR_FIELDS = [
  { key: 'primary',        label: 'Primary action',        hint: 'Settle, Send KOT, Print. Text colour auto-selected.' },
  { key: 'selected',       label: 'Selected / highlight',  hint: 'Selected item, active tab, chosen line.' },
  { key: 'accent',         label: 'Accent',                hint: 'Hover, secondary emphasis, focus.' },
  { key: 'page_bg',        label: 'Page background',       hint: 'The workspace behind cards and panels.' },
  { key: 'card_bg',        label: 'Card / surface',        hint: 'Cards, panels, dialogs and drawers.' },
  { key: 'raised_surface', label: 'Raised surface',        hint: 'Order rail, table strip, subtle raised area.' },
  { key: 'border',         label: 'Border',                hint: 'Silver-lined hairline used throughout.' },
  { key: 'primary_text',   label: 'Primary text',          hint: 'Item names, prices, totals, labels.' },
  { key: 'secondary_text', label: 'Secondary text',        hint: 'Hints, muted copy, table meta.' },
];

const FONT_OPTIONS = [
  'Inter', 'Manrope', 'Plus Jakarta Sans',
  'IBM Plex Sans', 'Source Sans 3', 'DM Sans', 'System UI',
];
const WEIGHT_OPTIONS = [
  { value: 'regular',  label: 'Regular'    },
  { value: 'medium',   label: 'Medium'     },
  { value: 'semibold', label: 'Semi-bold'  },
];
const RADIUS_OPTIONS = [
  { value: 'sharp',   label: 'Sharp'   },
  { value: 'soft',    label: 'Soft'    },
  { value: 'rounded', label: 'Rounded' },
];
const ELEVATION_OPTIONS = [
  { value: 'flat',    label: 'Flat'          },
  { value: 'soft',    label: 'Soft'          },
  { value: 'premium', label: 'Premium glass' },
];

const HEX_RE = /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i;
const normaliseHex = (v) => {
  if (!v) return '';
  let h = v.trim();
  if (!h.startsWith('#')) h = '#' + h;
  return h.toUpperCase();
};

function ColourInput({ value, onChange, id }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || '#FFFFFF'}
             onChange={(e) => onChange(e.target.value.toUpperCase())}
             className="h-9 w-11 rounded border border-[#D1D5D0] bg-white cursor-pointer p-0.5"
             aria-label="Colour picker"
             data-testid={`theme-color-${id}`} />
      <Input value={value || ''}
             onChange={(e) => onChange(e.target.value)}
             onBlur={(e) => {
               const n = normaliseHex(e.target.value);
               if (HEX_RE.test(n)) onChange(n);
             }}
             placeholder="#RRGGBB"
             className="uppercase font-mono text-xs w-28"
             data-testid={`theme-hex-${id}`} />
    </div>
  );
}

// Auto text-on-fill contrast preview — helps admins see the light /
// dark auto-selection at a glance. Testable via `auto-text-chip-<id>`.
function AutoTextChip({ fill, id }) {
  const t = bestTextOn(fill || '#FFFFFF');
  const ratio = contrastRatio(t, fill || '#FFFFFF');
  return (
    <div className="inline-flex items-center gap-1.5 text-[10px] tracking-wide"
         style={{ color: '#3F4A47' }}
         data-testid={`auto-text-chip-${id || 'fill'}`}>
      <span className="inline-block h-4 min-w-[26px] rounded px-1 text-center font-semibold"
            style={{ background: fill, color: t, border: '1px solid #D1D5D0' }}>
        Aa
      </span>
      <span>auto · {ratio.toFixed(1)}:1</span>
    </div>
  );
}

/**
 * Live preview — the SAME resolver + CSS variable map used by the live
 * terminal, mounted on a scoped root inside this component. The chunks
 * below deliberately mirror the actual terminal surfaces (service-mode
 * tabs, category tabs, item tiles, order line, totals, primary +
 * secondary action, payment mode, table tile).
 */
function LivePreview({ theme }) {
  const rootRef = useRef(null);
  useEffect(() => {
    if (!rootRef.current) return;
    // Compose an "effective"-shaped object from the local editor state.
    const effective = { ...theme };
    const vars = resolvePosTheme(effective);
    applyPosThemeVars(vars, rootRef.current);
    ensurePosFontLoaded(effective.font_family);
    if (effective.heading_font && effective.heading_font !== effective.font_family) {
      ensurePosFontLoaded(effective.heading_font);
    }
  }, [theme]);

  return (
    <div ref={rootRef}
         data-testid="theme-live-preview"
         className="pos-terminal-root rounded-[var(--pos-radius-lg,14px)] overflow-hidden"
         style={{
           background: 'var(--pos-color-page-bg)',
           border: '1px solid var(--pos-color-border)',
           boxShadow: 'var(--pos-shadow-md)',
           fontFamily: 'var(--pos-font-family)',
         }}>
      <div className="text-[10px] uppercase tracking-widest font-semibold px-3 py-2 flex items-center justify-between"
           style={{ background: 'var(--pos-color-raised-surface)', color: 'var(--pos-color-secondary-text)', borderBottom: '1px solid var(--pos-color-border)' }}>
        <span>Live preview · uses the same tokens as the terminal</span>
        <span className="tabular-nums">Table 12 · 2 covers</span>
      </div>
      <div className="p-3 space-y-2.5">
        {/* Service mode tabs */}
        <div className="flex items-center gap-1.5">
          {['Dine-in', 'Takeaway', 'Delivery', 'Room service'].map((s, i) => (
            <span key={s} className="text-[11px] px-2.5 py-1 rounded-[var(--pos-radius-sm,6px)]"
                  style={i === 0
                    ? { background: 'var(--pos-color-primary)', color: 'var(--pos-color-on-primary)', boxShadow: 'var(--pos-shadow-sm)' }
                    : { background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
              {s}
            </span>
          ))}
        </div>
        <div className="rounded-[var(--pos-radius-md,10px)] overflow-hidden"
             style={{ background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)', boxShadow: 'var(--pos-shadow-sm)' }}>
          {/* Category tabs */}
          <div className="flex items-center gap-1.5 px-3 pt-3">
            {['All', 'Starters', 'Mains', 'Drinks'].map((c, i) => (
              <span key={c} className="text-[11px] px-2.5 py-1 rounded-[var(--pos-radius-sm,6px)]"
                    style={i === 1
                      ? { background: 'var(--pos-color-selected)', color: 'var(--pos-color-on-selected)', border: '1px solid var(--pos-color-accent)' }
                      : { color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
                {c}
              </span>
            ))}
          </div>
          {/* Item tiles */}
          <div className="grid grid-cols-3 gap-2 p-3">
            {[
              { name: 'Paneer Tikka', price: '₹280' },
              { name: 'Butter Naan',  price: '₹80',  selected: true },
              { name: 'Masala Chai',  price: '₹60' },
            ].map((it) => (
              <div key={it.name}
                   className="rounded-[var(--pos-radius-md,10px)] text-[11px] px-2.5 py-2"
                   style={it.selected
                     ? { background: 'var(--pos-color-selected)', color: 'var(--pos-color-on-selected)', border: '1px solid var(--pos-color-accent)', boxShadow: 'var(--pos-shadow-sm)' }
                     : { background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
                <div className="font-medium truncate">{it.name}</div>
                <div className="text-[10px]" style={{ color: 'var(--pos-color-secondary-text)' }}>{it.price}</div>
              </div>
            ))}
          </div>
          {/* Order line + total */}
          <div className="border-t px-3 py-2 flex items-center justify-between text-[11px]"
               style={{ borderColor: 'var(--pos-color-border)' }}>
            <span>Butter Naan × 2</span>
            <span className="tabular-nums font-medium">₹160.00</span>
          </div>
          <div className="border-t px-3 py-2 flex items-center justify-between text-[12px]"
               style={{ borderColor: 'var(--pos-color-border)', background: 'var(--pos-color-raised-surface)' }}>
            <span style={{ color: 'var(--pos-color-secondary-text)' }}>Grand total</span>
            <span className="tabular-nums font-semibold" style={{ color: 'var(--pos-color-primary-text)' }}>₹520.00</span>
          </div>
          <div className="p-3 flex items-center gap-2 border-t"
               style={{ borderColor: 'var(--pos-color-border)' }}>
            <button type="button"
                    className="h-9 px-3 rounded-[var(--pos-radius-sm,6px)] text-[12px] font-semibold flex-1"
                    style={{ background: 'var(--pos-color-primary)', color: 'var(--pos-color-on-primary)', boxShadow: 'var(--pos-shadow-sm)' }}>
              Settle · ₹520.00
            </button>
            <button type="button"
                    className="h-9 px-3 rounded-[var(--pos-radius-sm,6px)] text-[12px]"
                    style={{ background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
              Send KOT
            </button>
          </div>
        </div>
        {/* Payment modes + table tile row */}
        <div className="flex items-center gap-2">
          <div className="rounded-[var(--pos-radius-sm,6px)] text-[10px] px-2 py-1"
               style={{ background: 'var(--pos-color-selected)', color: 'var(--pos-color-on-selected)', border: '1px solid var(--pos-color-accent)' }}>
            Cash
          </div>
          <div className="rounded-[var(--pos-radius-sm,6px)] text-[10px] px-2 py-1"
               style={{ background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
            Card
          </div>
          <div className="rounded-[var(--pos-radius-sm,6px)] text-[10px] px-2 py-1"
               style={{ background: 'var(--pos-color-card-bg)', color: 'var(--pos-color-primary-text)', border: '1px solid var(--pos-color-border)' }}>
            UPI
          </div>
          <div className="flex-1" />
          <div className="rounded-[var(--pos-radius-sm,6px)] text-[10px] px-2 py-1"
               style={{ background: '#065F46', color: '#FFFFFF' }}>
            Success · operational
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PosAppearanceSection({ outletId, outletName }) {
  const { API, getHeaders } = useAuth();
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [saved,   setSaved]     = useState(null);
  const [mode,    setMode]      = useState('default');
  const [scope,   setScope]     = useState('outlet');
  const [colours, setColours]   = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [issues,  setIssues]    = useState([]);
  const [dirty,   setDirty]     = useState(false);

  const fetchState = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pos/theme`, {
        headers: getHeaders(),
        params: { outlet_id: outletId },
      });
      setSaved(r.data);
      setDefaults(r.data.defaults);
      const seed = r.data.outlet_saved || r.data.tenant_saved || r.data.defaults;
      setColours({ ...r.data.defaults, ...(seed || {}) });
      if (r.data.outlet_saved) { setScope('outlet'); setMode('custom'); }
      else if (r.data.tenant_saved) { setScope('tenant'); setMode('custom'); }
      else { setScope('outlet'); setMode('default'); }
      setDirty(false);
      setIssues([]);
    } catch (e) {
      toast.error('Could not load POS theme');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletId]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const setField = (k, v) => {
    setColours((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
    setMode('custom');
  };

  // Client-side contrast preview (matches server rules 1:1).
  useEffect(() => {
    if (mode !== 'custom' || !colours) { setIssues([]); return; }
    const found = [];
    const on = bestTextOn(colours.primary || '#000');
    if (contrastRatio(on, colours.primary || '#000') < 4.5)
      found.push('Primary action button text cannot reach 4.5:1 contrast on the chosen background.');
    if (contrastRatio(bestTextOn(colours.card_bg || '#FFF'), colours.card_bg || '#FFF') < 4.5)
      found.push('Card text cannot reach 4.5:1 contrast on the chosen surface.');
    if (contrastRatio(bestTextOn(colours.page_bg || '#FFF'), colours.page_bg || '#FFF') < 4.5)
      found.push('Page text cannot reach 4.5:1 contrast on the chosen background.');
    if (contrastRatio(colours.primary_text || '#000', colours.card_bg || '#FFF') < 4.5)
      found.push('Primary text is not readable on the card background (needs 4.5:1).');
    if (contrastRatio(colours.secondary_text || '#000', colours.card_bg || '#FFF') < 3.0)
      found.push('Secondary text is not readable on the card background (needs 3:1).');
    if (contrastRatio(colours.selected || '#FFF', colours.card_bg || '#FFF') < 1.15)
      found.push('Selected/highlight colour is too close to the card background to be visible.');
    setIssues(found);
  }, [mode, colours]);

  const previewTheme = useMemo(() => {
    if (!colours) return defaults;
    return mode === 'default' ? defaults : colours;
  }, [mode, colours, defaults]);

  const reset = async () => {
    if (!window.confirm('Reset this outlet to the RestroPulse default POS theme?')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/pos/theme`,
        { outlet_id: scope === 'outlet' ? outletId : null, mode: 'default' },
        { headers: getHeaders() });
      toast.success('Reverted to RestroPulse default');
      fetchState();
    } catch (e) {
      toast.error('Could not reset theme');
    } finally { setSaving(false); }
  };

  const save = async () => {
    if (mode === 'custom' && issues.length > 0) {
      toast.error('Fix the contrast issues before saving');
      return null;
    }
    setSaving(true);
    try {
      const body = {
        outlet_id: scope === 'outlet' ? outletId : null,
        mode,
      };
      if (mode === 'custom') body.theme = colours;
      const r = await axios.put(`${API}/pos/theme`, body, { headers: getHeaders() });
      toast.success(`POS theme saved (${scope === 'outlet' ? 'outlet override' : 'tenant default'})`);
      fetchState();
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && Array.isArray(d.contrast_issues)) {
        setIssues(d.contrast_issues);
        toast.error('Contrast validation failed. See warnings below.');
      } else {
        toast.error(typeof d === 'string' ? d : 'Could not save theme');
      }
      return null;
    } finally { setSaving(false); }
  };

  const saveAndOpenTerminal = async () => {
    if (!outletId) { toast.error('Select an outlet first'); return; }
    // Save first — no bypass query params. Then open the terminal in a
    // new tab so it reads the actual saved theme from the cascade.
    if (dirty || mode === 'custom') {
      const res = await save();
      if (!res) return;
    }
    const url = `${window.location.origin}/pos?outlet_id=${encodeURIComponent(outletId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading || !colours || !previewTheme) {
    return (
      <Card className="bg-white border-[#E2E8DF] mt-6">
        <CardContent className="p-6 text-center text-[#5C6B64]">Loading appearance…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E2E8DF] mt-6" data-testid="pos-appearance">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold flex items-center gap-1.5">
              <Palette className="w-3.5 h-3.5" /> POS appearance
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-[#1A2421] mt-1">Terminal theme</h3>
            <p className="text-xs text-[#5C6B64] mt-1 max-w-2xl">
              Governs the entire POS terminal — colour, typography, shape and elevation. Applies only
              to the operational terminal, never to the admin platform, KOTs or printed receipts.
              Operational status colours (success, warning, offline, destructive, unsent KOT) remain
              system-controlled so they stay recognisable.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={reset} disabled={saving}
                    className="border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)]"
                    data-testid="theme-reset">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to default
            </Button>
            <Button variant="outline" onClick={saveAndOpenTerminal} disabled={saving || !outletId}
                    className="border-[#E2E8DF] text-[#1A2421] hover:text-[color:var(--color-primary)]"
                    data-testid="theme-view-on-terminal">
              <ExternalLink className="w-4 h-4 mr-1.5" /> View on terminal
            </Button>
            <Button onClick={save} disabled={saving || (mode === 'custom' && issues.length > 0)}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="theme-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save theme'}
            </Button>
          </div>
        </div>

        {/* Scope + mode */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Apply to</Label>
            <RadioGroup value={scope} onValueChange={(v) => { setScope(v); setDirty(true); }}
                        className="mt-2 flex gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-[#1A2421]" data-testid="theme-scope-outlet">
                <RadioGroupItem value="outlet" />
                This outlet <span className="text-[#5C6B64]">({outletName || 'selected'})</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-[#1A2421]" data-testid="theme-scope-tenant">
                <RadioGroupItem value="tenant" />
                Whole tenant (default for all outlets)
              </label>
            </RadioGroup>
            <div className="text-[11px] text-[#5C6B64] mt-1">
              An outlet-level override takes precedence over the tenant-level theme.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Theme</Label>
            <RadioGroup value={mode} onValueChange={(v) => { setMode(v); setDirty(true); }}
                        className="mt-2 flex gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-[#1A2421]" data-testid="theme-mode-default">
                <RadioGroupItem value="default" />
                RestroPulse Default
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-[#1A2421]" data-testid="theme-mode-custom">
                <RadioGroupItem value="custom" />
                Custom POS Theme
              </label>
            </RadioGroup>
            <div className="text-[11px] text-[#5C6B64] mt-1">
              {mode === 'default'
                ? 'Uses the standard RestroPulse premium palette.'
                : 'Pick tokens below. Text colours are auto-selected for readability.'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ─── Left column: token editors ─── */}
          <div className={`space-y-6 ${mode === 'default' ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Colours */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2 flex items-center gap-1.5">
                <Palette className="w-3 h-3" /> Colours
              </div>
              <div className="space-y-3">
                {COLOUR_FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-[1fr_auto] items-start gap-3">
                    <div>
                      <Label className="text-sm font-medium text-[#1A2421]">{f.label}</Label>
                      <div className="text-[11px] text-[#5C6B64] mt-0.5">{f.hint}</div>
                      {(f.key === 'primary' || f.key === 'selected' || f.key === 'accent') && (
                        <div className="mt-1"><AutoTextChip fill={colours[f.key]} id={f.key} /></div>
                      )}
                    </div>
                    <ColourInput id={f.key} value={colours[f.key]}
                                 onChange={(v) => setField(f.key, v)} />
                  </div>
                ))}
              </div>
            </div>

            {/* Typography */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2 flex items-center gap-1.5">
                <Type className="w-3 h-3" /> Typography
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-[#1A2421]">Interface font</Label>
                  <Select value={colours.font_family} onValueChange={(v) => setField('font_family', v)}>
                    <SelectTrigger className="mt-1 bg-white border-[#E2E8DF]" data-testid="theme-font-family">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((f) => (
                        <SelectItem key={f} value={f} data-testid={`theme-font-${f.replace(/\s+/g, '-')}`}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A2421]">Heading font</Label>
                  <Select value={colours.heading_font} onValueChange={(v) => setField('heading_font', v)}>
                    <SelectTrigger className="mt-1 bg-white border-[#E2E8DF]" data-testid="theme-heading-font">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-[#1A2421]">Weight preset</Label>
                  <RadioGroup value={colours.font_weight_preset}
                              onValueChange={(v) => setField('font_weight_preset', v)}
                              className="mt-2 flex flex-wrap gap-4">
                    {WEIGHT_OPTIONS.map((o) => (
                      <label key={o.value} className="inline-flex items-center gap-2 text-sm text-[#1A2421]"
                             data-testid={`theme-weight-${o.value}`}>
                        <RadioGroupItem value={o.value} /> {o.label}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>

            {/* Shape */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2 flex items-center gap-1.5">
                <Square className="w-3 h-3" /> Shape
              </div>
              <RadioGroup value={colours.radius_preset}
                          onValueChange={(v) => setField('radius_preset', v)}
                          className="flex flex-wrap gap-4">
                {RADIUS_OPTIONS.map((o) => (
                  <label key={o.value} className="inline-flex items-center gap-2 text-sm text-[#1A2421]"
                         data-testid={`theme-radius-${o.value}`}>
                    <RadioGroupItem value={o.value} /> {o.label}
                  </label>
                ))}
              </RadioGroup>
              <div className="text-[11px] text-[#5C6B64] mt-1">
                Sharp = 2–8px corners · Soft = 6–18px · Rounded = 10–24px.
              </div>
            </div>

            {/* Elevation */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Elevation
              </div>
              <RadioGroup value={colours.elevation_preset}
                          onValueChange={(v) => setField('elevation_preset', v)}
                          className="flex flex-wrap gap-4">
                {ELEVATION_OPTIONS.map((o) => (
                  <label key={o.value} className="inline-flex items-center gap-2 text-sm text-[#1A2421]"
                         data-testid={`theme-elevation-${o.value}`}>
                    <RadioGroupItem value={o.value} /> {o.label}
                  </label>
                ))}
              </RadioGroup>
              <div className="text-[11px] text-[#5C6B64] mt-1">
                Flat = hairline borders · Soft = subtle layered shadows · Premium = glass-like highlight.
              </div>
            </div>

            {issues.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3"
                   data-testid="theme-contrast-warning">
                <div className="font-semibold mb-1">Contrast warnings — cannot save</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {issues.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* ─── Right column: live preview ─── */}
          <div>
            <LivePreview theme={previewTheme} />
            <div className="text-[11px] text-[#5C6B64] mt-2">
              Preview uses the same theme resolver and CSS variable map as the live terminal.
              What you see here is what the cashier will see after saving.
            </div>
          </div>
        </div>

        {saved?.source && (
          <div className="text-[11px] text-[#5C6B64] mt-4" data-testid="theme-source-indicator">
            Currently applied: <strong className="text-[#1A2421]">
              {saved.source === 'outlet' ? 'outlet override'
                : saved.source === 'tenant' ? 'tenant default'
                : 'RestroPulse default'}
            </strong>
            {dirty ? <span className="ml-2 text-amber-700">· unsaved changes</span> : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
